import {
  FIREBASE_EVENT_MAX_PARAMS,
  FIREBASE_INTEGER_PARAM_NAMES,
  FIREBASE_STRING_VALUE_MAX_LENGTH,
  isBridgeAvailable,
  isFirebaseEventName,
  isFirebaseParamName,
  type AnalyticsParamValue,
} from '@sunflower-games/webview-bridge';

import { logToNewRelic } from '../debug';

import { clampBridgeString } from './clampBridgeString';
import { narrowParams } from './narrowParams';
import { getShellBridge } from './shellBridge';
import { hasShellCapability } from './shellPresence';

/** Whether the shell runs native Firebase Analytics, from its `app.info`. Never throws. */
export function shellHasNativeFirebase(): boolean {
  try {
    return hasShellCapability('firebaseAnalytics');
  } catch {
    return false;
  }
}

// Appended to the user agent by a shell build with native Firebase (mobile-shell `qaUserAgent.ts`).
const NATIVE_FIREBASE_USER_AGENT_MARKER = 'sunflower-shell-firebase';

const hasNativeFirebaseMarker = () => navigator.userAgent.includes(NATIVE_FIREBASE_USER_AGENT_MARKER);

/**
 * A synchronous hint for boot code that cannot wait for shell presence: `false` only when this page surely gets no
 * native Firebase. The bridge global can land after this page's scripts, so the shell's user-agent marker counts too.
 */
export function mayHaveNativeFirebase(): boolean {
  return isBridgeAvailable() || hasNativeFirebaseMarker();
}

// How long after navigation a page with the marker waits for the bridge global before giving up on it.
export const NATIVE_FIREBASE_BRIDGE_WAIT_MS = 3000;
const BRIDGE_POLL_MS = 50;

/** The shell's marker is on the page but its bridge global is not yet, and the wait for it has not run out. */
export function isAwaitingNativeFirebaseBridge(): boolean {
  return !isBridgeAvailable() && hasNativeFirebaseMarker() && performance.now() < NATIVE_FIREBASE_BRIDGE_WAIT_MS;
}

/** Resolves once the bridge global lands or the wait runs out; at once when nothing is awaited. Never rejects. */
export function waitForNativeFirebaseBridge(): Promise<void> {
  return new Promise((resolve) => {
    const poll = () => {
      if (isAwaitingNativeFirebaseBridge()) setTimeout(poll, BRIDGE_POLL_MS);
      else resolve();
    };
    poll();
  });
}

const reportedDrops = new Set<string>();

/** Test seam: forget which drops were reported. */
export function resetReportedDrops(): void {
  reportedDrops.clear();
}

// Once per page per key, so a broken name or bridge does not log on every event.
const reportDrop = (key: string, details: Record<string, string>) => {
  if (reportedDrops.has(key)) return;
  reportedDrops.add(key);
  logToNewRelic('Shell Firebase event dropped', details, 'warn');
};

/** A name Firebase takes from an app: other characters become `_`, a refused name gets an `app_` prefix. `null` if still refused. */
export function toFirebaseEventName(name: string): string | null {
  const sanitized = name.replace(/[^A-Za-z0-9_]/g, '_').slice(0, 40);
  if (isFirebaseEventName(sanitized)) return sanitized;
  const prefixed = `app_${sanitized}`.slice(0, 40);
  return isFirebaseEventName(prefixed) ? prefixed : null;
}

// The shell drops a non-number under these names, so one must not take a slot from a param it keeps.
const INTEGER_PARAM_NAMES: ReadonlySet<string> = new Set(FIREBASE_INTEGER_PARAM_NAMES);

const toParamValue = (value: unknown): AnalyticsParamValue | undefined => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string') return clampBridgeString(value, FIREBASE_STRING_VALUE_MAX_LENGTH);
  return undefined;
};

/** Narrows params to what the shell's schema accepts (it drops the whole event otherwise); earlier keys win the cap. */
export function toNativeFirebaseParams(params: Record<string, unknown>): Record<string, AnalyticsParamValue> {
  return narrowParams(
    params,
    isFirebaseParamName,
    (raw, key) => (INTEGER_PARAM_NAMES.has(key) && typeof raw !== 'number' ? undefined : toParamValue(raw)),
    FIREBASE_EVENT_MAX_PARAMS,
  );
}

/**
 * Sends one GA4 event to the shell's native Firebase (the app's GA4 app stream), renaming one Firebase refuses.
 * @returns Whether the shell took it; a drop is reported to New Relic.
 */
export function sendShellFirebaseEvent(name: string, params: Record<string, unknown>): boolean {
  const firebaseName = toFirebaseEventName(name);
  if (firebaseName === null) {
    reportDrop(`name:${name}`, { name, reason: 'invalid-name' });
    return false;
  }

  const bridge = getShellBridge();
  if (bridge === null) {
    reportDrop('no-bridge', { name, reason: 'no-bridge' });
    return false;
  }

  try {
    bridge.send('analytics.firebaseEvent', { name: firebaseName, params: toNativeFirebaseParams(params) });
    return true;
  } catch (error) {
    reportDrop('send-failed', { name, reason: 'send-failed', error: String(error) });
    return false;
  }
}
